import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import { useKnowledgeBaseList } from '../../../../components/QAInput/KnowledgeBaseMention/hooks/useKnowledgeBaseApi';
import { useHistoryKbDocsRestore } from '../../../../components/QAInput/KnowledgeBaseMention/hooks/useKbDocs';
import {
  resolveKnowledgeFromDisplayQuery,
  resolveKbIdsNeedingDocsForHistory,
} from '../../../../components/QAInput/KnowledgeBaseMention/utils/resolveKnowledgeFromHistory';
import type { ISelectedKnowledgeTagData } from '../../../../components/QAInput/KnowledgeBaseMention/components/SelectedKnowledgeTag';
import { resolveEditDisplayQuery } from './resolveEditDisplayQuery';
import type { IEditQuestionFrontendMeta } from './types';

interface IUseEditMentionSessionParams {
  isEdit: boolean;
  question: string;
  frontend_metadata?: IEditQuestionFrontendMeta;
  isEN: boolean;
}

/**
 * 编辑态 mention 会话：单一数据流
 *
 * 阶段：
 * - hydrateEpoch 0：等待知识库列表 + selectedKnowledge 还原（无 @ 时跳过）
 * - hydrateEpoch 1：通知 QAInput 执行 DOM hydrate（仅此阶段及 sessionKey 变化时）
 *
 * 面板改选只更新 editKnowledge，不触发 hydrateEpoch，避免与 QAInput 内部状态冲突。
 */
export const useEditMentionSession = ({
  isEdit,
  question,
  frontend_metadata,
  isEN,
}: IUseEditMentionSessionParams) => {
  const { formatMessage } = useIntl();
  const kbDocUnit = formatMessage({ id: 'dba_kb_inline_doc_unit' });

  const hasKnowledgeMeta = useMemo(
    () =>
      Boolean(
        frontend_metadata?.display_query?.includes('@') ||
          frontend_metadata?.kb_expand_token,
      ),
    [frontend_metadata],
  );

  const { kbList } = useKnowledgeBaseList({
    ready: isEdit && hasKnowledgeMeta,
  });

  const displayQuery = useMemo(
    () => resolveEditDisplayQuery(frontend_metadata, question, isEN, kbDocUnit),
    [frontend_metadata, question, isEN, kbDocUnit],
  );

  const restoreDocKbIds = useMemo(
    () =>
      resolveKbIdsNeedingDocsForHistory(
        displayQuery,
        kbList,
        kbDocUnit,
        frontend_metadata?.kb_expand_token,
      ),
    [displayQuery, kbList, kbDocUnit, frontend_metadata?.kb_expand_token],
  );

  const { data: editKbList, historyDocsReady } = useHistoryKbDocsRestore({
    kbList,
    restoreDocKbIds,
    ready: isEdit && hasKnowledgeMeta && kbList.length > 0,
  });

  const [sessionKey, setSessionKey] = useState(0);
  /** 0=等待知识库还原；1=可 hydrate 编辑器 */
  const [hydrateEpoch, setHydrateEpoch] = useState(0);
  const [inputValue, setInputValue] = useState(() =>
    resolveEditDisplayQuery(frontend_metadata, question, isEN, kbDocUnit),
  );
  const [editKnowledge, setEditKnowledge] = useState<
  ISelectedKnowledgeTagData[]
  >([]);
  const knowledgeRestoredRef = useRef(false);

  const beginSession = useCallback(() => {
    knowledgeRestoredRef.current = false;
    setEditKnowledge([]);
    setInputValue(displayQuery);
    setSessionKey(key => key + 1);
    setHydrateEpoch(hasKnowledgeMeta ? 0 : 1);
  }, [displayQuery, hasKnowledgeMeta]);

  useEffect(() => {
    if (!isEdit) {
      knowledgeRestoredRef.current = false;
    }
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit || knowledgeRestoredRef.current) {
      return;
    }
    if (!hasKnowledgeMeta) {
      knowledgeRestoredRef.current = true;
      return;
    }
    if (!editKbList.length) {
      return;
    }
    if (!historyDocsReady) {
      return;
    }

    const resolved = resolveKnowledgeFromDisplayQuery(
      displayQuery,
      editKbList,
      kbDocUnit,
      frontend_metadata?.kb_expand_token,
    );

    if (resolved.length) {
      setEditKnowledge(resolved);
    }
    knowledgeRestoredRef.current = true;
    setHydrateEpoch(1);
  }, [
    isEdit,
    hasKnowledgeMeta,
    editKbList,
    historyDocsReady,
    displayQuery,
    kbDocUnit,
    frontend_metadata?.kb_expand_token,
  ]);

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  return {
    sessionKey,
    hydrateEpoch,
    displayQuery,
    inputValue,
    handleInputChange,
    editKnowledge,
    setEditKnowledge,
    beginSession,
  };
};
